import React, { useState, useRef, useEffect } from 'react';
import { QuestType } from '../types';
import { aiService } from '../services/aiService';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import { trackAIQuestGenerated } from '../services/analyticsService';
import { subscriptionService } from '../services/subscriptionService';

interface AIQuestGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onQuestsGenerated: (quests: any[]) => void;
  onOpenSubscription?: () => void; // Callback to open subscription settings
}

const AIQuestGenerator: React.FC<AIQuestGeneratorProps> = ({ isOpen, onClose, onQuestsGenerated, onOpenSubscription }) => {
  const { profile, quests, updateProfile, showNotification, isDemoMode } = useUser();
  const { t } = useLanguage();
  const [userGoal, setUserGoal] = useState('');
  const [questType, setQuestType] = useState<QuestType>('daily');
  const [questCount, setQuestCount] = useState(3);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Quest type options with icons
  const questTypeOptions = [
    { value: 'daily', label: t.quests.daily, iconPath: '/icons/icon-quest-daily.png' },
    { value: 'main', label: t.quests.main, iconPath: '/icons/icon-quest-main.png' },
    { value: 'instant', label: 'Instant', iconPath: '/icons/icon-quest-instant.png' },
    { value: 'boss', label: t.quests.boss, iconPath: '/icons/icon-quest-boss.png' },
  ];

  const selectedQuestType = questTypeOptions.find(opt => opt.value === questType);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
    };

    if (showTypeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypeDropdown]);

  if (!isOpen) return null;

  // If in demo mode, show signup prompt and close
  if (isDemoMode) {
    showNotification('🔒 Sign up to unlock AI quest generation and save your progress!', 'warning');
    onClose();
    return null;
  }

  const handleGenerate = async () => {
    if (!profile) return;

    // 🔒 CHECK LIMIT BEFORE GENERATION
    const limitCheck = subscriptionService.canUseAIQuestGeneration(profile);
    if (!limitCheck.allowed) {
      setError(limitCheck.reason || 'You\'ve reached your monthly limit');
      setShowUpgradePrompt(true);
      return;
    }

    setIsGenerating(true);
    setError('');
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setShowUpgradePrompt(false);

    try {
      const generatedQuests = await aiService.generateQuests(profile, quests, {
        userGoal: userGoal.trim(),
        questType,
        count: questCount,
        analyzePatterns: true
      });

      setSuggestions(generatedQuests);
      // Auto-select all suggestions
      setSelectedSuggestions(new Set(generatedQuests.map((_, i) => i)));
      
      // ✅ USAGE IS TRACKED SERVER-SIDE (Cloud Function already incremented it)
      // The server-side Cloud Function (generateQuestsWithAI) automatically tracks usage
      // No need to track client-side (would be blocked by security anyway)
      
      // Track AI quest generation in analytics
      trackAIQuestGenerated(userGoal.trim().length, generatedQuests.length);
      
      // Calculate remaining (server already incremented, so current value - 1)
      const remainingText = limitCheck.remaining === 'unlimited' 
        ? 'unlimited' 
        : Math.max(0, (limitCheck.remaining as number) - 1);
      showNotification(`✨ Generated ${generatedQuests.length} AI quests! (${remainingText} remaining this month)`, 'success');
    } catch (err: any) {
      setError(err.message || 'Failed to generate quests. Check your API key and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleSelection = (index: number) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSuggestions(newSelected);
  };

  const handleAcceptSelected = () => {
    const selected = suggestions.filter((_, i) => selectedSuggestions.has(i));
    if (selected.length > 0) {
      onQuestsGenerated(selected);
      handleClose();
    }
  };

  const handleClose = () => {
    setUserGoal('');
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setError('');
    onClose();
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400 border-green-500/50';
      case 'normal': return 'text-blue-400 border-blue-500/50';
      case 'hard': return 'text-orange-400 border-orange-500/50';
      case 'boss': return 'text-red-400 border-red-500/50';
      default: return 'text-gray-400 border-gray-500/50';
    }
  };

  const isConfigured = aiService.isConfigured();

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-50 p-4 modal-backdrop animate-fade-in overflow-y-auto">
      <div className="relative max-w-3xl w-full animate-scale-in">
        {/* Main Modal */}
        <div className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20 flex-shrink-0"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-orbitron text-system-blue tracking-wider uppercase">
                  ［ AI System ］
                </span>
              </div>
              <h2 className="text-lg sm:text-2xl font-bold font-orbitron text-white tracking-wider">
                {t.aiGenerator.questTitle.toUpperCase()}
              </h2>
              <p className="text-sm text-gray-400 font-rajdhani">
                {t.aiGenerator.questSubtitle}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white text-xl leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              ✕
            </button>
          </div>

          {/* Content - Scrollable area */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1 max-h-[calc(90vh-100px)]">
            {!isConfigured && (
              <div className="bg-gradient-to-b from-red-950/40 via-slate-950/30 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/40 border-r-2 border-b-2 border-r-red-700/60 border-b-red-700/60 rounded-3xl p-6"
                style={{
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <div className="font-orbitron font-bold text-red-400 mb-1">
                      {t.aiGenerator.configureAPI}
                    </div>
                    <div className="text-sm text-gray-400 font-rajdhani">
                      Add your OpenAI API key to <code className="bg-black/30 px-1 rounded">.env</code> file:
                      <br />
                      <code className="bg-black/30 px-2 py-1 rounded block mt-2">
                        REACT_APP_OPENAI_API_KEY=your_key_here
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Input Section */}
            {suggestions.length === 0 && (
              <div className="space-y-4">
                {/* Natural Language Input */}
                <div>
                  <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                    📝 {t.aiGenerator.yourGoal}
                  </label>
                  <textarea
                    value={userGoal}
                    onChange={(e) => setUserGoal(e.target.value)}
                    placeholder={t.aiGenerator.goalPlaceholder}
                    className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all min-h-[100px] resize-none placeholder-gray-400"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                    disabled={isGenerating || !isConfigured}
                  />
                </div>

                {/* Quest Type Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                      {t.aiGenerator.questType}
                    </label>
                    {/* Custom Dropdown */}
                    <div className="relative" ref={typeDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                        className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer flex items-center justify-between hover:border-blue-500/50 hover:scale-105"
                        style={{
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <img 
                            src={selectedQuestType?.iconPath} 
                            alt={selectedQuestType?.label}
                            className="w-5 h-5 object-contain"
                            style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                          />
                          <span>{selectedQuestType?.label}</span>
                        </div>
                        <span className="text-blue-400">▼</span>
                      </button>
                      
                      {showTypeDropdown && (
                        <div className="w-full mt-2 bg-slate-800/95 backdrop-blur-xl border border-blue-400/60 rounded-xl shadow-lg shadow-blue-500/30">
                          {questTypeOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setQuestType(option.value as QuestType);
                                setShowTypeDropdown(false);
                              }}
                              className={`w-full px-4 py-3 text-left font-sans transition-colors flex items-center gap-2 text-base first:rounded-t-xl last:rounded-b-xl ${
                                questType === option.value 
                                  ? 'bg-blue-500/20 text-blue-400' 
                                  : 'text-white hover:bg-slate-700/50'
                              }`}
                            >
                              <img 
                                src={option.iconPath} 
                                alt={option.label}
                                className="w-5 h-5 object-contain flex-shrink-0"
                                style={{ filter: questType === option.value ? 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' : 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                              />
                              <span>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-rajdhani text-gray-400 uppercase mb-2">
                      {t.aiGenerator.numberOfQuests}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={questCount}
                      onChange={(e) => setQuestCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                      className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                      disabled={isGenerating || !isConfigured}
                    />
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !isConfigured}
                  className={`relative w-full py-3 sm:py-4 rounded-2xl font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all ${
                    isGenerating || !isConfigured
                      ? 'bg-slate-700/50 text-gray-500 cursor-not-allowed border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60'
                      : 'bg-gradient-to-b from-blue-800/60 via-purple-800/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 hover:scale-105 text-white relative overflow-hidden'
                  }`}
                  style={{
                    boxShadow: isGenerating || !isConfigured ? '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)' : '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {isGenerating || !isConfigured ? (
                    <span>{t.aiGenerator.generating}</span>
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                      <span className="relative z-10">⚡ {t.aiGenerator.generate}</span>
                    </>
                  )}
                </button>

                {error && (
                  <div className="bg-gradient-to-b from-red-950/40 via-slate-950/30 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/40 border-r-2 border-b-2 border-r-red-700/60 border-b-red-700/60 rounded-3xl p-6"
                    style={{
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl flex-shrink-0">❌</span>
                      <div className="flex-1">
                        <div className="text-red-400 font-rajdhani mb-3">
                          {error}
                        </div>
                        {showUpgradePrompt && (
                          <button
                            onClick={() => {
                              onClose();
                              if (onOpenSubscription) {
                                onOpenSubscription();
                              }
                            }}
                            className="inline-block px-4 py-2 bg-gradient-to-b from-amber-800/60 to-orange-800/60 backdrop-blur-sm border-t-2 border-l-2 border-amber-500/50 border-r-2 border-b-2 border-r-orange-700/70 border-b-orange-700/70 rounded-xl text-white font-orbitron font-bold text-sm transition-all hover:scale-105"
                            style={{
                              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                            }}
                          >
                            ⬆️ Upgrade to S-Rank for Unlimited AI
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Suggestions Display */}
            {suggestions.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold font-orbitron text-system-blue">
                    ✨ {t.aiGenerator.generated} ({selectedSuggestions.size}/{suggestions.length} {t.aiGenerator.selected})
                  </h3>
                  <button
                    onClick={() => setSuggestions([])}
                    className="text-sm text-gray-400 hover:text-white font-rajdhani transition-all"
                  >
                    ← {t.aiGenerator.generateNew}
                  </button>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {suggestions.map((quest, index) => (
                    <div
                      key={index}
                      onClick={() => handleToggleSelection(index)}
                      className={`p-4 rounded-2xl transition-all cursor-pointer ${
                        selectedSuggestions.has(index)
                          ? 'bg-gradient-to-b from-blue-950/40 via-purple-950/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60'
                          : 'bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-blue-500/50'
                      }`}
                      style={{
                        boxShadow: selectedSuggestions.has(index) 
                          ? '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)' 
                          : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          selectedSuggestions.has(index) ? 'bg-system-blue border-system-blue' : 'border-gray-600'
                        }`}>
                          {selectedSuggestions.has(index) && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-orbitron font-bold text-white mb-1">
                            {quest.title}
                          </div>
                          <div className="text-sm text-gray-400 font-rajdhani mb-2">
                            {quest.description}
                          </div>
                          <div className="flex items-center gap-3 flex-wrap text-xs">
                            <span className={`px-2 py-1 rounded border font-rajdhani font-bold uppercase ${getDifficultyColor(quest.difficulty)}`}>
                              {quest.difficulty}
                            </span>
                            <span className="text-gray-500 font-rajdhani">
                              {quest.type}
                            </span>
                            {quest.estimatedDuration && (
                              <span className="text-purple-400 font-rajdhani">
                                ⏱️ {quest.estimatedDuration}
                              </span>
                            )}
                            {quest.resetDaily && (
                              <span className="text-blue-400 font-rajdhani">
                                🔄 Daily Reset
                              </span>
                            )}
                          </div>
                          {quest.reasoning && (
                            <div className="mt-2 text-xs text-gray-500 font-rajdhani italic border-l-2 border-purple-500/30 pl-2">
                              💡 {quest.reasoning}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 rounded-2xl font-orbitron font-bold text-sm tracking-wider transition-all relative overflow-hidden group"
                    style={{
                      background: 'linear-gradient(to bottom, rgb(51 65 85 / 0.4), rgb(30 41 59 / 0.4), rgb(15 23 42 / 0.4))',
                      border: '1px solid rgba(100, 116, 139, 0.6)',
                      borderTop: '2px solid rgba(100, 116, 139, 0.4)',
                      borderLeft: '2px solid rgba(100, 116, 139, 0.4)',
                      borderRight: '2px solid rgba(71, 85, 105, 0.6)',
                      borderBottom: '2px solid rgba(71, 85, 105, 0.6)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-2xl"></div>
                    <span className="relative z-10">{t.common.cancel.toUpperCase()}</span>
                  </button>
                  <button
                    onClick={handleAcceptSelected}
                    disabled={selectedSuggestions.size === 0}
                    className={`flex-1 py-3 rounded-2xl font-orbitron font-bold text-sm tracking-wider transition-all relative overflow-hidden group ${
                      selectedSuggestions.size === 0
                        ? 'text-gray-500 cursor-not-allowed'
                        : 'text-white transform hover:scale-[1.01]'
                    }`}
                    style={selectedSuggestions.size === 0 ? {
                      background: 'linear-gradient(to bottom, rgb(51 65 85 / 0.4), rgb(30 41 59 / 0.4), rgb(15 23 42 / 0.4))',
                      border: '1px solid rgba(100, 116, 139, 0.6)',
                      borderTop: '2px solid rgba(100, 116, 139, 0.4)',
                      borderLeft: '2px solid rgba(100, 116, 139, 0.4)',
                      borderRight: '2px solid rgba(71, 85, 105, 0.6)',
                      borderBottom: '2px solid rgba(71, 85, 105, 0.6)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                    } : {
                      background: 'linear-gradient(to bottom, rgb(16 185 129 / 0.8), rgb(5 150 105 / 0.8), rgb(4 120 87 / 0.8))',
                      border: '1px solid rgba(34, 197, 94, 0.6)',
                      borderTop: '2px solid rgba(34, 197, 94, 0.4)',
                      borderLeft: '2px solid rgba(34, 197, 94, 0.4)',
                      borderRight: '2px solid rgba(5, 150, 105, 0.6)',
                      borderBottom: '2px solid rgba(5, 150, 105, 0.6)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                    <span className="relative z-10">✓ {t.aiGenerator.acceptSelected}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIQuestGenerator;

